import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import MiniPlayer from "@/components/MiniPlayer";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import {
  Search, Home, Tv, Radio, Heart, User, Sun, Moon, Menu, X,
  LayoutDashboard, Shield, LogOut, MapPin, PlusCircle, Trophy, Languages,
  Loader2, BadgeCheck, CornerDownLeft, Clock,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const RECENT_KEY = "rdc_recent_searches";
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const pushRecent = (entry) => {
  try {
    const list = readRecent().filter((e) =>
      entry.kind === "media" ? e.id !== entry.id : !(e.kind === "query" && e.q === entry.q)
    );
    list.unshift({ ...entry, ts: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
  } catch {}
};

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const toggle = () => i18n.changeLanguage(lang === "fr" ? "en" : "fr");
  return (
    <button onClick={toggle} data-testid="lang-switcher" title="Français / English"
      className="flex items-center gap-1 h-9 px-2.5 rounded-full hover:bg-secondary text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
      <Languages size={17} /> <span className="uppercase">{lang}</span>
    </button>
  );
}

function Logo() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
      <span className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-rdcblue text-white font-heading font-black">
        <span>CN</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rdcred border-2 border-background" />
      </span>
      <span className="hidden sm:block leading-none">
        <span className="font-heading font-extrabold text-[15px] tracking-tight block">CHAÎNES NUMÉRIQUES</span>
        <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">RD Congo</span>
      </span>
    </Link>
  );
}

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { if (open) setRecent(readRecent()); }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch {}
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (m) => {
    pushRecent({ kind: "media", id: m.id, name: m.name, type: m.type, slug: m.slug, logo: m.logo, province: m.province, certified: m.certified });
    onClose();
    setQ("");
    navigate(`/${m.type}/${m.slug}`);
  };

  const runQuery = (text) => {
    const term = (text || "").trim();
    if (!term) return;
    pushRecent({ kind: "query", q: term });
    onClose();
    setQ("");
    navigate(`/browse?q=${encodeURIComponent(term)}`);
  };

  const submit = (e) => { e.preventDefault(); runQuery(q); };

  const clearRecent = () => { try { localStorage.removeItem(RECENT_KEY); } catch {} setRecent([]); };
  const removeRecent = (entry) => {
    const list = readRecent().filter((e) => (entry.kind === "media" ? e.id !== entry.id : !(e.kind === "query" && e.q === entry.q)));
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
    setRecent(list);
  };

  const highlight = (text) => {
    if (!q.trim()) return text;
    const i = text.toLowerCase().indexOf(q.trim().toLowerCase());
    if (i === -1) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark className="bg-rdcgold/25 text-foreground rounded px-0.5">{text.slice(i, i + q.trim().length)}</mark>
        {text.slice(i + q.trim().length)}
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl p-0 gap-0 top-[12%] translate-y-0 overflow-hidden rounded-2xl border-border/70 shadow-2xl shadow-rdcblue/10"
        data-testid="search-overlay"
      >
        <form
          onSubmit={submit}
          className="flex items-center gap-3 border-b border-border/70 bg-gradient-to-r from-rdcblue/[0.06] via-transparent to-rdcgold/[0.06] px-4 py-3.5"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-rdcblue/10 text-rdcblue shrink-0">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </span>
          <input
            autoFocus
            data-testid="search-input-main"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une chaîne TV, une radio, une province…"
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              data-testid="search-clear"
              className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <X size={15} />
            </button>
          )}
        </form>

        <div className="max-h-[22rem] overflow-y-auto p-2">
          {results.map((m, i) => (
            <button
              key={m.id}
              onClick={() => go(m)}
              data-testid={`search-result-${m.slug}`}
              style={{ animationDelay: `${i * 35}ms` }}
              className="group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-secondary animate-[fadeUp_.3s_ease_both]"
            >
              <img src={m.logo} alt="" className="w-11 h-11 rounded-xl bg-white object-cover ring-1 ring-border shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {highlight(m.name)}
                  {m.certified && <BadgeCheck size={14} className="text-rdcblue shrink-0" />}
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium ${m.type === "tv" ? "bg-rdcblue/10 text-rdcblue" : "bg-rdcgold/10 text-rdcgold"}`}>
                    {m.type === "tv" ? <Tv size={10} /> : <Radio size={10} />} {m.type === "tv" ? "TV" : "Radio"}
                  </span>
                  <span className="flex items-center gap-1 truncate"><MapPin size={10} /> {m.province}</span>
                </p>
              </div>
              <CornerDownLeft size={15} className="text-muted-foreground/0 group-hover:text-muted-foreground transition-colors shrink-0" />
            </button>
          ))}

          {loading && results.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
              <Loader2 size={22} className="animate-spin text-rdcblue" />
              <p className="text-sm">Recherche en cours…</p>
            </div>
          )}
          {!loading && q && results.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-secondary text-muted-foreground"><Search size={20} /></span>
              <p className="text-sm text-muted-foreground">Aucun résultat pour <span className="font-semibold text-foreground">“{q}”</span>.</p>
              <p className="text-xs text-muted-foreground/70">Essayez le nom d'une chaîne ou d'une province.</p>
            </div>
          )}
          {!q && (
            <>
              {recent.length > 0 ? (
                <div className="animate-[fadeUp_.3s_ease_both]">
                  <div className="flex items-center justify-between px-3 pt-1 pb-1.5">
                    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Clock size={13} /> Recherches récentes
                    </span>
                    <button
                      onClick={clearRecent}
                      data-testid="search-recent-clear"
                      className="text-xs font-medium text-muted-foreground hover:text-rdcred transition-colors"
                    >
                      Effacer
                    </button>
                  </div>
                  {recent.map((e, i) =>
                    e.kind === "media" ? (
                      <div key={`m-${e.id}`} className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-secondary transition-colors">
                        <button onClick={() => go(e)} data-testid={`search-recent-${e.slug}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <img src={e.logo} alt="" className="w-10 h-10 rounded-xl bg-white object-cover ring-1 ring-border shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                              {e.name}
                              {e.certified && <BadgeCheck size={14} className="text-rdcblue shrink-0" />}
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                              <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium ${e.type === "tv" ? "bg-rdcblue/10 text-rdcblue" : "bg-rdcgold/10 text-rdcgold"}`}>
                                {e.type === "tv" ? <Tv size={10} /> : <Radio size={10} />} {e.type === "tv" ? "TV" : "Radio"}
                              </span>
                              <span className="flex items-center gap-1 truncate"><MapPin size={10} /> {e.province}</span>
                            </p>
                          </div>
                        </button>
                        <button onClick={() => removeRecent(e)} data-testid={`search-recent-remove-${e.slug}`} className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground/50 hover:bg-background hover:text-rdcred transition-colors shrink-0" title="Retirer">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div key={`q-${i}`} className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-secondary transition-colors">
                        <button onClick={() => runQuery(e.q)} data-testid={`search-recent-query-${i}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-secondary text-muted-foreground shrink-0"><Clock size={16} /></span>
                          <p className="text-sm font-medium truncate">{e.q}</p>
                        </button>
                        <button onClick={() => removeRecent(e)} data-testid={`search-recent-remove-q-${i}`} className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground/50 hover:bg-background hover:text-rdcred transition-colors shrink-0" title="Retirer">
                          <X size={14} />
                        </button>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                  <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-rdcblue/10 text-rdcblue"><Search size={20} /></span>
                  <p className="text-sm text-muted-foreground">Commencez à taper pour rechercher</p>
                  <p className="text-xs text-muted-foreground/70">Chaînes TV, radios, provinces…</p>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const NAV = [
  { to: "/", key: "nav.home", icon: Home, testid: "nav-home" },
  { to: "/browse?type=tv", key: "nav.tv", icon: Tv, testid: "nav-tv" },
  { to: "/browse?type=radio", key: "nav.radio", icon: Radio, testid: "nav-radio" },
  { to: "/provinces", key: "nav.provinces", icon: MapPin, testid: "nav-provinces" },
  { to: "/rankings", key: "nav.rankings", icon: Trophy, testid: "nav-rankings" },
  { to: "/favorites", key: "nav.favorites", icon: Heart, testid: "nav-favorites" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hasMedia, setHasMedia] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) { setHasMedia(false); return; }
    api.get("/me/my-media").then((r) => setHasMedia((r.data || []).length > 0)).catch(() => {});
  }, [user]);

  const showMediaSpace = !!user && (user.role === "manager" || user.role === "admin" || hasMedia);

  const doLogout = useCallback(async () => { await logout(); navigate("/"); }, [logout, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 glass bg-background/80 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`${n.testid}-desktop`}
                className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                {t(n.key)}
              </Link>
            ))}
          </nav>

          <div className="flex-1 min-w-0 flex justify-center px-2 sm:px-4">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="search-trigger"
              className="group flex items-center gap-2.5 w-full max-w-2xl rounded-full border border-border bg-secondary/40 pl-2 pr-4 py-1.5 text-sm text-muted-foreground shadow-sm transition-all hover:border-rdcblue/40 hover:bg-secondary/70 hover:text-foreground hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rdcblue/40"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-rdcblue/10 text-rdcblue transition-colors group-hover:bg-rdcblue/15">
                <Search size={16} />
              </span>
              <span className="flex-1 text-left truncate">{t("common.search_placeholder")}</span>
            </button>
          </div>

          <LanguageSwitcher />

          <button onClick={toggle} data-testid="theme-toggle" className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {user && <NotificationBell />}

          {user ? (
            <div className="hidden lg:flex items-center gap-2">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="w-9 h-9 rounded-full bg-rdcred/10 text-rdcred flex items-center justify-center hover:bg-rdcred/20" title={t("common.admin")}>
                  <Shield size={18} />
                </Link>
              )}
              {showMediaSpace && (
                <Link to="/manager" data-testid="nav-manager" className="w-9 h-9 rounded-full bg-rdcgold/10 text-rdcgold flex items-center justify-center hover:bg-rdcgold/20" title={t("common.media_space")}>
                  <LayoutDashboard size={18} />
                </Link>
              )}
              <Link to="/profile" data-testid="nav-profile-desktop" className="flex items-center gap-2 rounded-full hover:bg-secondary pl-1 pr-3 py-1">
                {user.avatar ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full" /> :
                  <span className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">{user.first_name?.[0] || "U"}</span>}
                <span className="text-sm font-medium max-w-24 truncate">{user.first_name}</span>
              </Link>
              <button onClick={doLogout} data-testid="logout-btn" className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground"><LogOut size={17} /></button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-btn" className="hidden lg:inline-flex items-center rounded-full bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-rdcblue transition-colors">
              {t("common.login")}
            </Link>
          )}

          <button onClick={() => setMenuOpen((m) => !m)} data-testid="mobile-menu-btn" className="lg:hidden w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-border bg-background px-4 py-3 space-y-1">
            <Link to="/register-media" className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-secondary text-sm font-medium"><PlusCircle size={17} /> {t("common.register_media")}</Link>
            {user?.role === "admin" && <Link to="/admin" className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-secondary text-sm font-medium"><Shield size={17} /> {t("common.admin")}</Link>}
            {showMediaSpace && <Link to="/manager" className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-secondary text-sm font-medium"><LayoutDashboard size={17} /> {t("common.media_space")}</Link>}
            {user ? (
              <button onClick={doLogout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-secondary text-sm font-medium text-rdcred"><LogOut size={17} /> {t("common.logout")}</button>
            ) : (
              <Link to="/login" className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold justify-center"><User size={17} /> {t("common.login_register")}</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 pb-28 lg:pb-10">
        <Outlet />
      </main>

      <footer className="hidden lg:block border-t border-border mt-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Les Chaînes Numériques de la RDC</span>
          <div className="flex items-center gap-4">
            <Link to="/register-media" className="hover:text-primary transition-colors" data-testid="footer-register-media">{t("footer.register")}</Link>
            <Link to="/manager/login" className="hover:text-primary transition-colors" data-testid="footer-manager-login">{t("footer.manager")}</Link>
            <Link to="/admin/login" className="hover:text-rdcred transition-colors" data-testid="footer-admin-login">{t("footer.admin")}</Link>
          </div>
        </div>
      </footer>

      <MiniPlayer />

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 glass bg-background/90 border-t border-border">
        <div className="grid grid-cols-5">
          {[
            { to: "/", label: t("nav.home"), icon: Home, testid: "mobile-nav-home-tab" },
            { to: "/browse?type=tv", label: t("nav.tv"), icon: Tv, testid: "mobile-nav-tv-tab" },
            { to: "/browse?type=radio", label: t("nav.radio"), icon: Radio, testid: "mobile-nav-radio-tab" },
            { to: user ? "/favorites" : "/login", label: t("nav.favorites"), icon: Heart, testid: "mobile-nav-favorites-tab" },
            { to: user ? "/profile" : "/login", label: t("nav.profile"), icon: User, testid: "mobile-nav-profil-tab" },
          ].map((n) => {
            const active = location.pathname === n.to.split("?")[0];
            return (
              <Link key={n.label} to={n.to} data-testid={n.testid}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>
                <n.icon size={20} /> {n.label}
              </Link>
            );
          })}
        </div>
      </nav>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
